import { useEffect, useState } from "react";
import { useSession } from "@/lib/auth";
import { api } from "@/lib/api";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import PageShell from "@/components/ui/PageShell";

type Status = {
  discordLinked: boolean;
  discordUserId: string | null;
  hasMembership: boolean;
  guildJoinAttempted: boolean;
  guildJoinError: string | null;
  inviteUrl: string | null;
};

const bodyStyle = {
  fontFamily: fonts.body,
  fontWeight: 500,
  color: `${colors.navy}bb`,
  letterSpacing: "0.03em",
};

const labelStyle = {
  fontFamily: fonts.body,
  fontWeight: 700,
  color: colors.navy,
};

export function Community() {
  const theme = usePalette();
  const { data: session } = useSession();
  const [status, setStatus] = useState<Status | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.user) return;
    api
      .get<Status>("/api/account/community/status")
      .then(setStatus)
      .catch((e) => setErr(e instanceof Error ? e.message : "Failed to load status"));
  }, [session]);

  const linkUrl = `${import.meta.env.VITE_API_URL ?? ""}/api/auth/sign-in/social?provider=discord&callbackURL=${encodeURIComponent("/account/community")}`;

  if (!session?.user) {
    return (
      <PageShell title="Community access" eyebrow="Discord">
        <p style={bodyStyle}>Sign in first.</p>
      </PageShell>
    );
  }

  return (
    <PageShell title="Community access" eyebrow="Discord">
      {err && (
        <p
          className="mb-4 text-sm"
          style={{ color: colors.berryRose, fontFamily: fonts.body, fontWeight: 600 }}
        >
          {err}
        </p>
      )}
      {!status ? (
        <p style={bodyStyle}>Loading…</p>
      ) : (
        <div className="space-y-4">
          <p style={bodyStyle}>
            Discord linked:{" "}
            <strong style={labelStyle}>
              {status.discordLinked ? `yes (${status.discordUserId})` : "not yet"}
            </strong>
          </p>
          <p style={bodyStyle}>
            Active membership:{" "}
            <strong style={labelStyle}>{status.hasMembership ? "yes" : "no"}</strong>
          </p>

          {!status.discordLinked && (
            <p>
              <a
                href={linkUrl}
                className="underline transition-colors"
                style={{
                  fontFamily: fonts.body,
                  fontWeight: 700,
                  color: theme.accent,
                  letterSpacing: "0.03em",
                }}
              >
                Link Discord →
              </a>
            </p>
          )}

          {status.discordLinked && status.hasMembership && (
            <>
              <p style={bodyStyle}>
                You should see the member role in the Discord server. Drift is reconciled every 15
                minutes.
              </p>
              {status.inviteUrl && (
                <p>
                  <a
                    href={status.inviteUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="underline"
                    style={{
                      fontFamily: fonts.body,
                      fontWeight: 700,
                      color: theme.accent,
                      letterSpacing: "0.03em",
                    }}
                  >
                    Open Discord invite →
                  </a>
                </p>
              )}
              {status.guildJoinError && (
                <p
                  style={{ color: colors.berryRose, fontFamily: fonts.body, fontWeight: 600 }}
                >
                  Discord auto-join did not complete. Use the invite link, then refresh this page.
                </p>
              )}
            </>
          )}

          {status.discordLinked && !status.hasMembership && (
            <p style={bodyStyle}>
              Linked. Once your membership is active, the bot will grant your role.
            </p>
          )}
        </div>
      )}
    </PageShell>
  );
}
